import React, { useMemo } from 'react';
import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { determineFilterStoreScope } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedPipelineAnalysisConfiguration } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getPrimaryFilterFullKey } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FilterMenuSinglePanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuSinglePanel';
import { FilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/FilterListBox';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { tryTranslateDynamic } from 'libs/end-to-end/src/phrase-utils';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';

export const PipelineAnalysisMenu = ({
  store,
  feature,
  databaseRevisionId,
  databaseId,
  organizationId,
  closeMenu,
  setPrimaryFilters,
  focusArea,
  analysisName,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  closeMenu: () => void;
  setPrimaryFilters: (params: {
    selections: PrimaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
}) => {
  const { myObservation } = useObservation();
  const language = useLanguage();

  const currentPipelineAnalysisConfiguration =
    getSelectedPipelineAnalysisConfiguration({
      feature,
      organizationId,
      databaseRevisionId,
      databaseId,
      store,
      focusArea,
      analysisName,
    });

  const items = useMemo(() => {
    const pipelineAnalysisConfigurations =
      myObservation?.pipelineAnalysisConfiguration ?? [];

    const fullKey = getPrimaryFilterFullKey({
      primaryFilterKey: 'pipelineAnalysisConfiguration',
    });

    const scope = determineFilterStoreScope({
      feature,
      fullKey,
      databaseId,
      databaseRevisionId,
      organizationId,
      focusArea,
      analysisName,
    });

    if (!scope) {
      return [];
    }

    return pipelineAnalysisConfigurations.map(
      (pipelineAnalysisConfiguration) => ({
        value: pipelineAnalysisConfiguration.name,
        translatedContent: tryTranslateDynamic({
          logger: uiAppLogger,
          phrase: pipelineAnalysisConfiguration.name,
          language,
          customValue: pipelineAnalysisConfiguration.customFirstPhaseName ?? '',
        }),
        selected:
          pipelineAnalysisConfiguration.name ===
          currentPipelineAnalysisConfiguration,
      }),
    );
  }, [
    myObservation?.pipelineAnalysisConfiguration,
    feature,
    databaseId,
    databaseRevisionId,
    organizationId,
    focusArea,
    analysisName,
    language,
    currentPipelineAnalysisConfiguration,
  ]);

  return (
    <FilterMenuSinglePanel
      filterContent={
        <FilterListBox
          multiple={false}
          items={items}
          onUpdate={({ addedSelections }) => {
            const primaryFiltersSelections: PrimaryFilterSelection = {
              primaryFilterKey: 'pipelineAnalysisConfiguration',
              value: addedSelections[0],
            };

            setPrimaryFilters({ selections: [primaryFiltersSelections] });

            closeMenu();
          }}
        />
      }
      bottomProps={'none'}
    />
  );
};
